import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Layout/Header";
import BottomNav from "../../components/Layout/BottomNav";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import globalStyle from "../../styles/globalStyle";
import adminService from "../../services/adminService";
import {
  formatXRP, // Added formatXRP function
} from "../../utils/formatters";

import {
  UsersIcon,
  DocumentCheckIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BanknotesIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState<any[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [dashboardStats, pendingKyc, pendingDeposits, pendingWithdrawals] =
        await Promise.all([
          adminService.getDashboardStats(),
          adminService.getPendingKyc(),
          adminService.getPendingDeposits(),
          adminService.getPendingWithdrawals(),
        ]);

      setStats(dashboardStats);

      // Combine recent activities
      const activities = [
        ...pendingKyc.map((kyc: any) => ({
          type: "kyc",
          title: "KYC Submission",
          description: `${
            kyc.user?.username || "User"
          } submitted KYC documents`,
          time: new Date(kyc.created_at),
          priority: "high",
        })),
        ...pendingDeposits.slice(0, 3).map((deposit: any) => ({
          type: "deposit",
          title: "Deposit Request",
          description: `${deposit.user?.username || "User"} deposited ${
            deposit.amount
          } XRP`,
          time: new Date(deposit.created_at),
          priority: "medium",
        })),
        ...pendingWithdrawals.slice(0, 3).map((withdrawal: any) => ({
          type: "withdrawal",
          title: "Withdrawal Request",
          description: `${withdrawal.user?.username || "User"} withdrawing ${
            withdrawal.amount
          } XRP`,
          time: new Date(withdrawal.created_at),
          priority: "medium",
        })),
      ]
        .sort((a, b) => b.time.getTime() - a.time.getTime())
        .slice(0, 5);

      setRecentActivities(activities);
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to safely format XRP balance using formatXRP
  const formatBalance = (balance: any) => {
    const num =
      balance !== undefined && balance !== null
        ? typeof balance === "string"
          ? parseFloat(balance)
          : Number(balance)
        : 0;

    if (isNaN(num)) {
      return "0.00 XRP";
    }

    return `${formatXRP(num)} XRP`;
  };

  // Helper function to safely format XRP amounts (without "XRP" suffix)
  const formatXRPAmount = (amount: any) => {
    const num =
      amount !== undefined && amount !== null
        ? typeof amount === "string"
          ? parseFloat(amount)
          : Number(amount)
        : 0;

    if (isNaN(num)) {
      return "0.00";
    }

    return formatXRP(num);
  };

  const StatCard = ({ icon: Icon, title, value, change, color }: any) => (
    <Card hover padding="lg">
      <div className="flex items-start justify-between">
        <div>
          <p className={globalStyle.caption + " text-gray-400"}>{title}</p>
          <h3 className={`${globalStyle.h3} mt-2 text-white`}>
            {loading ? "..." : value}
          </h3>
          {change !== undefined && (
            <p
              className={`${globalStyle.caption} mt-1 ${
                change > 0 ? "text-green-500" : "text-red-500"
              }`}
            >
              {change > 0 ? "↑" : "↓"} {Math.abs(change)}% from last month
            </p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </Card>
  );

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "kyc":
        return DocumentCheckIcon;
      case "deposit":
        return ArrowTrendingUpIcon;
      case "withdrawal":
        return ArrowTrendingDownIcon;
      default:
        return ClockIcon;
    }
  };

  const getActivityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "text-red-500";
      case "medium":
        return "text-yellow-500";
      default:
        return "text-blue-500";
    }
  };

  return (
    <div className={globalStyle.container}>
      <Header />

      <main className="page-content2">
        <div className="page-content2-bg"></div>
        <div className="page-content2-wrapper">
          <div className="page-content2-content">
            {/* Dashboard Header */}
            <div className="mb-6 md:mb-8">
              <h1 className={globalStyle.h3}>Admin Dashboard</h1>
              <p className={globalStyle.body2 + " text-gray-400"}>
                Overview of platform statistics and activities
              </p>
            </div>

            {/* Stats Grid */}
            <div className={globalStyle.grid4}>
              <StatCard
                icon={UsersIcon}
                title="Total Users"
                value={loading ? "..." : stats?.users?.total || 0}
                change={12.5}
                color="bg-blue-900 text-blue-300"
              />

              <StatCard
                icon={ShieldCheckIcon}
                title="Active Users"
                value={loading ? "..." : stats?.users?.active || 0}
                change={8.3}
                color="bg-green-900 text-green-300"
              />

              <StatCard
                icon={DocumentCheckIcon}
                title="Pending KYC"
                value={loading ? "..." : stats?.users?.pending_kyc || 0}
                change={-5.2}
                color="bg-yellow-900 text-yellow-300"
              />

              <StatCard
                icon={BanknotesIcon}
                title="Total Balance"
                value={loading ? "..." : formatBalance(stats?.total_balance)}
                change={15.7}
                color="bg-purple-900 text-purple-300"
              />
            </div>

            {/* Financial Overview */}
            <div className={globalStyle.grid2}>
              <Card padding="lg" className="mt-6 md:mt-8">
                <h3 className={globalStyle.h5}>Today's Transactions</h3>
                <div className="mt-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-green-900 flex items-center justify-center mr-3">
                        <ArrowTrendingUpIcon className="w-5 h-5 text-green-300" />
                      </div>
                      <div>
                        <p className="font-medium text-white">Total Deposits</p>
                        <p className={globalStyle.caption + " text-gray-400"}>
                          Today
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-green-500">
                        +
                        {loading
                          ? "0.00"
                          : formatXRPAmount(
                              stats?.transactions?.today_deposits,
                            )}{" "}
                        XRP
                      </p>
                      <p className={globalStyle.caption + " text-gray-400"}>
                        Completed
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-red-900 flex items-center justify-center mr-3">
                        <ArrowTrendingDownIcon className="w-5 h-5 text-red-300" />
                      </div>
                      <div>
                        <p className="font-medium text-white">
                          Total Withdrawals
                        </p>
                        <p className={globalStyle.caption + " text-gray-400"}>
                          Today
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-red-500">
                        -
                        {loading
                          ? "0.00"
                          : formatXRPAmount(
                              stats?.transactions?.today_withdrawals,
                            )}{" "}
                        XRP
                      </p>
                      <p className={globalStyle.caption + " text-gray-400"}>
                        Processed
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-blue-900 flex items-center justify-center mr-3">
                        <ClockIcon className="w-5 h-5 text-blue-300" />
                      </div>
                      <div>
                        <p className="font-medium text-white">
                          Pending Deposits
                        </p>
                        <p className={globalStyle.caption + " text-gray-400"}>
                          Awaiting confirmation
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-white">
                        {loading
                          ? "0.00"
                          : formatXRPAmount(
                              stats?.transactions?.pending_deposits,
                            )}{" "}
                        XRP
                      </p>
                      <p className={globalStyle.caption + " text-gray-400"}>
                        Pending
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-yellow-900 flex items-center justify-center mr-3">
                        <ClockIcon className="w-5 h-5 text-yellow-300" />
                      </div>
                      <div>
                        <p className="font-medium text-white">
                          Pending Withdrawals
                        </p>
                        <p className={globalStyle.caption + " text-gray-400"}>
                          Awaiting processing
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-white">
                        {loading
                          ? "0.00"
                          : formatXRPAmount(
                              stats?.transactions?.pending_withdrawals,
                            )}{" "}
                        XRP
                      </p>
                      <p className={globalStyle.caption + " text-gray-400"}>
                        Pending
                      </p>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Recent Activities */}
              <Card padding="lg" className="mt-6 md:mt-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className={globalStyle.h5}>Recent Activities</h3>
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={loadDashboardData}
                    loading={loading}
                  >
                    Refresh
                  </Button>
                </div>

                <div className="space-y-4">
                  {loading ? (
                    <div className="text-center py-8">
                      <svg
                        className={`${globalStyle.loadingSpinner} mx-auto`}
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <p className={`${globalStyle.body2} mt-4 text-gray-400`}>
                        Loading activities...
                      </p>
                    </div>
                  ) : recentActivities.length > 0 ? (
                    recentActivities.map((activity, index) => {
                      const ActivityIcon = getActivityIcon(activity.type);
                      const colorClass = getActivityColor(activity.priority);
                      const bgColorClass = colorClass.replace("text", "bg");

                      return (
                        <div
                          key={index}
                          className="flex items-start space-x-3 pb-4 border-b border-gray-800 last:border-0 last:pb-0"
                        >
                          <div
                            className={`p-2 rounded-lg ${bgColorClass.replace(
                              "-500",
                              "-900",
                            )} ${colorClass}`}
                          >
                            <ActivityIcon className="w-4 h-4" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm text-white">
                              {activity.title}
                            </p>
                            <p
                              className={globalStyle.caption + " text-gray-400"}
                            >
                              {activity.description}
                            </p>
                          </div>
                          <div className="text-right">
                            <p
                              className={globalStyle.caption + " text-gray-400"}
                            >
                              {activity.time.toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </p>
                            <p
                              className={globalStyle.caption + " text-gray-400"}
                            >
                              {activity.time.toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8">
                      <p className={globalStyle.body2 + " text-gray-400"}>
                        No recent activities
                      </p>
                    </div>
                  )}
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Button
                    variant="secondary"
                    fullWidth
                    onClick={() => navigate("/admin/kyc")}
                  >
                    View All KYC
                  </Button>
                  <Button
                    variant="secondary"
                    fullWidth
                    onClick={() => navigate("/admin/transactions")}
                  >
                    View Transactions
                  </Button>
                </div>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="mt-6 md:mt-8">
              <h3 className={`${globalStyle.h5} mb-4`}>Quick Actions</h3>
              <div className={globalStyle.grid4}>
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => navigate("/admin/users")}
                >
                  <UsersIcon className="w-4 h-4 mr-2 text-primary" />
                  Manage Users
                </Button>
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => navigate("/admin/kyc")}
                >
                  <DocumentCheckIcon className="w-4 h-4 mr-2 text-primary" />
                  Review KYC
                </Button>
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => navigate("/admin/transactions?type=deposit")}
                >
                  <ArrowTrendingUpIcon className="w-4 h-4 mr-2 text-primary" />
                  Confirm Deposits
                </Button>
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() =>
                    navigate("/admin/transactions?type=withdrawal")
                  }
                >
                  <ArrowTrendingDownIcon className="w-4 h-4 mr-2 text-primary" />
                  Process Withdrawals
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <BottomNav />
    </div>
  );
};

export default AdminDashboard;
